/**
 * SellerCoinPanel — loyalty coins on an order the shop rings up (v149.63).
 *
 * Shown in the checkout's seller mode (Itemized bill / New order) right
 * under the customer's phone. As the shop types the phone or email it asks
 * `POST /action-hub/products/orders/manual/coins` who that is — a read-only
 * lookup, it never opens an account — and shows their balance, how many
 * coins this bill can take (the counter rules New Bill → Total bill uses),
 * and what the bill earns them. The coins to use start at the most allowed,
 * as on Total bill; the shop can lower it.
 *
 * The parent owns `coins` (what's applied) and reads `onQuote` for the
 * discount; the server checks everything again when the order is placed.
 */
import { useEffect, useRef, useState } from "react";
import { Coins, Loader2, UserPlus } from "lucide-react";
import { api } from "../../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const digits = (s) => String(s || "").replace(/\D/g, "");
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n = (v) => Number(v || 0).toLocaleString("en-IN");

/** The customer is identifiable once the phone has 10 digits or the email looks whole. */
export function customerKey(phone, email) {
  const p = digits(phone);
  const e = String(email || "").trim().toLowerCase();
  return {
    phone: p.length >= 12 || (p.length === 10) ? phone : null,
    email: EMAIL_RE.test(e) ? e : null,
  };
}

/** Coins earned on what's paid: `earn_per_100` per whole ₹100. */
export function coinsEarned(quote, paid) {
  const rate = Number(quote?.rules?.earn_per_100 || 0);
  return paid > 0 && rate > 0 ? Math.floor(paid / 100) * rate : 0;
}

/** The discount the applied coins give, never past what this bill allows. */
export function coinDiscount(quote, coins) {
  if (!quote?.enabled) return 0;
  const use = Math.max(0, Math.min(Number(coins) || 0, Number(quote.max_usable) || 0));
  return Math.round(use * Number(quote.rules?.value_per_coin_inr || 0) * 100) / 100;
}

export default function SellerCoinPanel({ phone, email, bill, coins, setCoins, onQuote, disabled }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const touched = useRef({ key: null, touched: false });
  const key = customerKey(phone, email);
  const who = `${key.phone || ""}|${key.email || ""}`;

  // Ask again whenever the customer or the bill changes (debounced).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/action-hub/products/orders/manual/coins", {
          phone: key.phone || undefined, email: key.email || undefined, bill_inr: Math.max(0, Number(bill) || 0),
        });
        if (cancelled) return;
        setQuote(data);
        onQuote?.(data);
        // Start at the most this bill can take (Total bill does the same);
        // once the shop has moved it for this customer, keep their choice
        // unless the bill no longer allows it.
        const max = Number(data?.max_usable || 0);
        if (touched.current.key !== who) touched.current = { key: who, touched: false };
        if (!touched.current.touched) setCoins(max);
        else setCoins((c) => Math.min(Number(c) || 0, max));
      } catch {
        if (!cancelled) { setQuote(null); onQuote?.(null); setCoins(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, key.phone || key.email ? 350 : 0);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, bill]);

  if (!quote && !loading) return null;
  if (quote && !quote.enabled) return null;
  const coinName = quote?.coin_name || "coins";
  const c = quote?.customer;
  const max = Number(quote?.max_usable || 0);
  const use = Math.max(0, Math.min(Number(coins) || 0, max));
  const off = coinDiscount(quote, use);
  const earns = coinsEarned(quote, Math.max(0, (Number(bill) || 0) - off));
  const change = (v) => {
    touched.current = { key: who, touched: true };
    setCoins(Math.max(0, Math.min(max, Math.floor(Number(String(v).replace(/[^0-9]/g, "")) || 0))));
  };

  return (
    <div className="border-2 border-ink bg-sun p-3 space-y-2 shadow-brutalSm min-w-0" data-testid="seller-coin-panel">
      <div className="flex items-center gap-2 min-w-0">
        <Coins className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
        <p className="text-[11px] font-black uppercase tracking-wider flex-1 min-w-0 truncate">Loyalty {coinName}</p>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" data-testid="seller-coin-loading" />}
      </div>

      {!quote?.identified ? (
        <p className="text-[12px] leading-snug" data-testid="seller-coin-hint">
          Add the customer&apos;s phone or email to use their {coinName}
          {Number(quote?.rules?.earn_per_100) > 0 ? " and let them earn more" : ""}.
        </p>
      ) : c?.found ? (
        <>
          <p className="text-[12px] leading-snug min-w-0 break-words" data-testid="seller-coin-customer">
            <b>{c.name || "Customer"}</b> · balance <b data-testid="seller-coin-balance">{n(c.balance)}</b> {coinName}
          </p>
          {max > 0 ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <input
                  type="range" min={0} max={max} step={1} value={use} disabled={disabled}
                  onChange={(e) => change(e.target.value)}
                  aria-label={`${coinName} to use`}
                  className="flex-1 min-w-0 accent-coral h-8"
                  data-testid="seller-coin-slider"
                />
                <input
                  type="text" inputMode="numeric" value={String(use)} disabled={disabled}
                  onChange={(e) => change(e.target.value)}
                  aria-label={`${coinName} to use (number)`}
                  className="w-20 min-w-0 border-2 border-ink bg-white px-1.5 py-1 text-sm font-black tabular-nums text-right"
                  data-testid="seller-coin-input"
                />
              </div>
              <p className="text-[12px] font-bold flex flex-wrap justify-between gap-x-2" data-testid="seller-coin-discount">
                <span>Use {n(use)} {coinName}</span><span>−{inr(off)}</span>
              </p>
              <p className="text-[10px] text-ink/70 leading-snug">
                Up to {n(max)} on this bill ({quote.rules.max_pct}% cap) · 1 {coinName.replace(/s$/, "")} = ₹{Number(quote.rules.value_per_coin_inr).toFixed(2)}
              </p>
            </div>
          ) : (
            <p className="text-[12px] leading-snug" data-testid="seller-coin-blocked">
              {quote.blocked || `No ${coinName} to use yet.`}
            </p>
          )}
        </>
      ) : (
        <p className="text-[12px] leading-snug inline-flex items-start gap-1.5" data-testid="seller-coin-new">
          <UserPlus className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
          <span>New customer — no {coinName} yet.</span>
        </p>
      )}

      {quote?.identified && earns > 0 && (
        <p className="text-[12px] font-bold text-emerald-800" data-testid="seller-coin-earn">
          Earns +{n(earns)} {coinName} on this bill
        </p>
      )}
    </div>
  );
}
